<template>
  <div class="st-page">
    <div class="st-content">
      <!-- 左栏：策略模板库 -->
      <div class="st-col st-col--left ops-basic-bg">
        <div class="st-panel-title">
          策略模板库
          <el-tooltip content="用户级保存，多套策略随选随用，最多可创建 15 个模板" placement="top">
            <el-icon class="tip"><InfoFilled /></el-icon>
          </el-tooltip>
        </div>
        <el-input v-model="tplKeyword" placeholder="搜索模板名称" clearable size="small" class="st-tpl-search">
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>

        <div class="st-tpl-list" v-loading="loadingTemplates">
          <TemplateCard
            v-for="tpl in filteredTemplates"
            :key="tpl.id"
            :template="tpl"
            :selected="selectedId === tpl.id"
            @select="onSelectTemplate"
            @edit="onEditTemplate"
            @duplicate="onDuplicateTemplate"
            @delete="onDeleteTemplate"
          />
          <el-empty v-if="!filteredTemplates.length" description="暂无模板，点击下方新建" :image-size="70" />
        </div>

        <el-button class="st-tpl-add" type="primary" :icon="Plus" @click="onNewTemplate">新建模板</el-button>
      </div>

      <!-- 中栏 -->
      <div class="st-col st-col--center">
        <!-- ① 当前策略 -->
        <div class="st-block ops-basic-bg">
          <div class="st-block__head">
            <span class="st-step">1</span>
            <span class="st-block__title">当前策略</span>
          </div>
          <div v-if="selectedTemplate" class="st-current">
            <div class="st-current__row">
              <span class="st-current__name">{{ selectedTemplate.template_name }}</span>
              <span class="st-current__tag">{{ currentTag }}</span>
              <el-button-group class="st-view-toggle">
                <el-button :class="{ 'is-active': previewMode === 'chart' }" :icon="Grid" @click="previewMode = 'chart'" />
                <el-button :class="{ 'is-active': previewMode === 'list' }" :icon="List" @click="previewMode = 'list'" />
              </el-button-group>
            </div>
            <div class="st-current__meta">
              更新时间：{{ selectedTemplate.updated_time || '--' }}　创建人：{{ selectedTemplate.create_by || '--' }}
            </div>
            <div class="st-current__memo">备注：{{ selectedTemplate.memo || '--' }}</div>
            <!-- 图表模式 -->
            <div v-if="previewMode === 'chart'" class="st-preview-grid">
              <div class="st-preview">
                <div class="st-preview__title">工作日策略预览</div>
                <VChart class="st-preview__chart" :option="workOption" autoresize />
              </div>
              <div class="st-preview">
                <div class="st-preview__title">休息日策略预览</div>
                <VChart class="st-preview__chart" :option="restOption" autoresize />
              </div>
            </div>
            <!-- 列表模式 -->
            <div v-else class="st-list-wrap">
              <el-tabs v-model="listTab" class="st-list-tabs">
                <el-tab-pane label="工作日策略" name="work">
                  <el-table :data="selectedTemplate.strategys_work || []" size="small" border class="st-list-table">
                    <el-table-column label="开始" prop="start_time" align="center" min-width="70" />
                    <el-table-column label="结束" prop="end_time" align="center" min-width="70" />
                    <el-table-column label="功率(kW)" prop="power" align="center" min-width="80" />
                    <el-table-column label="上限(%)" prop="upper_limit" align="center" min-width="70" />
                    <el-table-column label="下限(%)" prop="lower_limit" align="center" min-width="70" />
                  </el-table>
                </el-tab-pane>
                <el-tab-pane label="休息日策略" name="rest">
                  <el-table :data="selectedTemplate.strategys_rest || []" size="small" border class="st-list-table">
                    <el-table-column label="开始" prop="start_time" align="center" min-width="70" />
                    <el-table-column label="结束" prop="end_time" align="center" min-width="70" />
                    <el-table-column label="功率(kW)" prop="power" align="center" min-width="80" />
                    <el-table-column label="上限(%)" prop="upper_limit" align="center" min-width="70" />
                    <el-table-column label="下限(%)" prop="lower_limit" align="center" min-width="70" />
                  </el-table>
                </el-tab-pane>
              </el-tabs>
            </div>
          </div>
          <el-empty v-else description="请从左侧选择或新建一套策略模板" :image-size="80" />
        </div>

        <!-- ② 目标选择 -->
        <div class="st-block ops-basic-bg st-target">
          <div class="st-block__head">
            <span class="st-step">2</span>
            <span class="st-block__title">目标选择</span>
            <div class="st-target__stat">
              <div class="stat-item"><span class="v">{{ stats.stationCount }}</span><span class="l">电站</span></div>
              <div class="stat-item"><span class="v">{{ stats.deviceCount }}</span><span class="l">台设备</span></div>
              <el-button type="primary" class="st-dispatch-btn" :icon="Promotion" :disabled="!canDispatch" @click="onDispatch">立即下发</el-button>
            </div>
          </div>
          <div class="st-target__body">
            <TargetTree ref="treeRef" @change="onTargetChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- 模板编辑 -->
    <TemplateEditorDialog ref="editorRef" @saved="onTemplateSaved" />
    <!-- 下发进度 -->
    <DispatchProgressPanel ref="dispatchRef" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import VChart from 'vue-echarts'
import { ElMessageBox } from 'element-plus'
import { Search, Plus, InfoFilled, Promotion, Grid, List } from '@element-plus/icons-vue'

import TemplateCard from './components/TemplateCard.vue'
import TargetTree from './components/TargetTree.vue'
import TemplateEditorDialog from './components/TemplateEditorDialog.vue'
import DispatchProgressPanel from './components/DispatchProgressPanel.vue'

import { listTemplates, deleteTemplate, createTemplate } from './api.js'
import { createOption, templateSummaryTag, copyTemplateName, MAX_TEMPLATES } from './utils.js'

// 作为运营详情 Tab 使用时，父级会透传 base-data/elink-data 等，这里统一接收避免落到根元素
defineProps({
  baseData: { type: Object, default: () => ({}) },
  elinkData: { type: Array, default: () => [] },
  loadingElink: { type: Boolean, default: false },
  powerTrackingData: { type: Array, default: () => [] },
})

const editorRef = ref(null)
const dispatchRef = ref(null)
const treeRef = ref(null)
const previewMode = ref('chart')
const listTab = ref('work')

const templates = ref([])
const tplKeyword = ref('')
const selectedId = ref(null)
const loadingTemplates = ref(false)

const targets = ref([])
const stats = ref({ stationCount: 0, deviceCount: 0 })

async function refreshTemplates() {
  loadingTemplates.value = true
  try {
    const res = await listTemplates()
    templates.value = (res?.code === 200 && Array.isArray(res.data)) ? res.data : []
  } catch (e) {
    console.warn('获取模板列表失败', e)
    templates.value = []
  } finally {
    loadingTemplates.value = false
  }
  if (!templates.value.find((t) => t.id === selectedId.value)) {
    selectedId.value = templates.value[0]?.id || null
  }
}
refreshTemplates()

const filteredTemplates = computed(() => {
  const kw = tplKeyword.value.trim()
  if (!kw) return templates.value
  return templates.value.filter((t) => (t.template_name || '').includes(kw))
})

const selectedTemplate = computed(() => templates.value.find((t) => t.id === selectedId.value) || null)
const currentTag = computed(() => (selectedTemplate.value ? templateSummaryTag(selectedTemplate.value) : ''))

// 预览甘特图（复用单站策略图表工具）
// 覆盖 tooltip：不透明背景 + 抬高层级 + confine 限制在图表内，避免溢出撑高页面/被遮挡
function previewOption(list) {
  const opt = createOption(list || [])
  opt.tooltip = {
    ...opt.tooltip,
    backgroundColor: '#FFFFFFF5',
    borderColor: 'rgba(0, 0, 0, 0.08)',
    borderWidth: 1,
    confine: true,
    extraCssText: 'z-index: 99999; border-radius: 8px; padding: 4px 10px; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14);',
  }
  return opt
}
const workOption = computed(() => previewOption(selectedTemplate.value?.strategys_work))
const restOption = computed(() => previewOption(selectedTemplate.value?.strategys_rest))

const canDispatch = computed(() => !!selectedTemplate.value && targets.value.length > 0)

function onSelectTemplate(tpl) {
  selectedId.value = tpl.id
}

function reachTemplateLimit() {
  if (templates.value.length >= MAX_TEMPLATES) {
    window.dispatchNotify({ type: 'info', message: `最多可创建 ${MAX_TEMPLATES} 个模板` })
    return true
  }
  return false
}

function onNewTemplate() {
  if (reachTemplateLimit()) return
  editorRef.value?.open(null)
}

function onEditTemplate(tpl) {
  editorRef.value?.open(tpl)
}

async function onDuplicateTemplate(tpl) {
  if (reachTemplateLimit()) return
  const res = await createTemplate({
    template_name: copyTemplateName(tpl.template_name, templates.value.map((t) => t.template_name)),
    memo: tpl.memo,
    strategy_mode: tpl.strategy_mode || '0',
    strategys_work: tpl.strategys_work || [],
    strategys_rest: tpl.strategys_rest || [],
  })
  if (res?.code === 200) {
    window.dispatchNotify({ type: 'success', message: '已复制为新模板' })
    await refreshTemplates()
  } else {
    window.dispatchNotify({ type: 'error', message: res?.msg || '复制失败' })
  }
}

async function onDeleteTemplate(tpl) {
  try {
    await ElMessageBox.confirm(`确定删除模板「${tpl.template_name}」？`, '提示', {
      type: 'warning', confirmButtonText: '确定', cancelButtonText: '取消', customClass: 'st-confirm-box',
    })
  } catch { return }
  const res = await deleteTemplate(tpl.id)
  if (res?.code === 200) {
    window.dispatchNotify({ type: 'success', message: '删除成功' })
    if (selectedId.value === tpl.id) selectedId.value = null
    await refreshTemplates()
  } else {
    window.dispatchNotify({ type: 'error', message: res?.msg || '删除失败' })
  }
}
// 下发前确认封装
function ElMessageBoxConfirm(msg) {
  return ElMessageBox.confirm(msg, '提示', { type: 'warning', confirmButtonText: '确定', cancelButtonText: '取消', customClass: 'st-confirm-box' })
}

async function onTemplateSaved() {
  await refreshTemplates()
}

function onTargetChange({ targets: t, stats: s }) {
  targets.value = t
  stats.value = s
}

async function onSaveAsNew() {
  if (!selectedTemplate.value) {
    window.dispatchNotify({ type: 'info', message: '请先选择策略模板' })
    return
  }
  if (reachTemplateLimit()) return
  const res = await createTemplate({
    template_name: copyTemplateName(selectedTemplate.value.template_name, templates.value.map((t) => t.template_name)),
    memo: selectedTemplate.value.memo,
    strategy_mode: selectedTemplate.value.strategy_mode || '0',
    strategys_work: selectedTemplate.value.strategys_work || [],
    strategys_rest: selectedTemplate.value.strategys_rest || [],
  })
  if (res?.code === 200) {
    window.dispatchNotify({ type: 'success', message: '已保存为新模板' })
    await refreshTemplates()
  } else {
    window.dispatchNotify({ type: 'error', message: res?.msg || '保存失败' })
  }
}

function onDispatch() {
  if (!canDispatch.value) return
  ElMessageBoxConfirm(`策略批量下发为高危操作，可能引发收益异常、设备充放电异常等情况，请谨慎确认。\n\n确定向 ${targets.value.length} 台设备下发「${selectedTemplate.value.template_name}」？`)
    .then(() => {
      dispatchRef.value?.start(targets.value, selectedTemplate.value)
      // 下发后清空目标选择
      treeRef.value?.clearChecked()
    })
    .catch(() => {})
}
</script>

<style scoped lang="scss">
.st-page {
  display: flex;
  flex-direction: column;
  min-height: 560px;
  height: 100%;
}

.ops-basic-bg {
  padding: 14px 16px;
  border-radius: 18px;
  border: 0.5px solid #FFF;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
}

.st-content {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 16px;
  overflow: hidden;
}

.st-col {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* 左栏 */
.st-col--left {
  min-height: 0;
}

.st-panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #2e2e2e;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;

  .tip { color: rgba(46, 46, 46, 0.35); font-size: 14px; }
}

.st-tpl-search {
  margin-bottom: 12px;
  :deep(.el-input__wrapper) { border-radius: 999px; }
}

.st-tpl-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 2px;
}

.st-tpl-add {
  margin-top: 12px;
  width: 100%;
  border-style: dashed;
  color: #fff;
  background: #3A68FF;
  border-color: #3A68FF;

  &:hover {
    color: #fff;
    background: #5A80FF;
    border-color: #5A80FF;
  }
}

/* 中栏 */
.st-col--center {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}

.st-block__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.st-step {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #3A68FF;
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.st-block__title {
  font-size: 15px;
  font-weight: 600;
  color: #2e2e2e;
}

.st-current__row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.st-view-toggle {
  margin-left: auto;
  display: flex;
  gap: 4px;

  .el-button {
    width: 32px;
    height: 32px;
    padding: 0;
    font-size: 16px;
    border-radius: 8px;
    border: 1px solid rgba(58, 104, 255, 0.25);
    color: rgba(46, 46, 46, 0.45);
    background: transparent;

    &.is-active {
      color: #3A68FF;
      background: rgba(58, 104, 255, 0.1);
      border-color: #3A68FF;
    }

    &:hover:not(.is-active) {
      color: #3A68FF;
      border-color: rgba(58, 104, 255, 0.5);
    }
  }
}

.st-current__name {
  font-size: 16px;
  font-weight: 600;
  color: #2e2e2e;
}

.st-current__tag {
  font-size: 12px;
  color: #3A68FF;
  background: rgba(58, 104, 255, 0.1);
  border-radius: 8px;
  padding: 2px 8px;
}

.st-current__edit { margin-left: auto; }

.st-current__meta {
  font-size: 12px;
  color: rgba(46, 46, 46, 0.5);
  margin: 6px 0 4px;
}

.st-current__memo {
  font-size: 12px;
  color: rgba(46, 46, 46, 0.5);
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 固定预览/列表区高度，切换时父容器不变 */
.st-preview-grid,
.st-list-wrap {
  height: 220px;
  overflow: hidden;
}

.st-preview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.st-list-wrap {
  display: flex;
  flex-direction: column;

  .st-list-tabs {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    :deep(.el-tabs__header) { margin-bottom: 6px; }
    :deep(.el-tabs__content) {
      flex: 1;
      min-height: 0;
      overflow: auto;
    }
  }

  .st-list-table {
    width: 100%;
    :deep(.el-table__header th) { background: #F6FAFF; }
  }
}

.st-preview__title {
  font-size: 13px;
  color: rgba(46, 46, 46, 0.7);
  margin-bottom: 6px;
}

.st-preview__chart {
  height: 200px;
}

.st-target {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.st-target__body {
  flex: 1;
  min-height: 260px;
  max-height: 420px;
}

.st-target__stat {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;

  .stat-item {
    display: flex;
    align-items: baseline;
    gap: 4px;
    .v { font-size: 16px; font-weight: 700; color: #3A68FF; }
    .l { font-size: 11px; color: rgba(8, 8, 8, 0.911); }
  }
}

.st-dispatch-btn {
  margin-left: 8px;
  --el-button-bg-color: #3A68FF;
  --el-button-border-color: #3A68FF;
  --el-button-text-color: #fff;
  --el-button-hover-bg-color: #5A80FF;
  --el-button-hover-border-color: #5A80FF;
  --el-button-hover-text-color: #fff;
  --el-button-active-bg-color: #2F56D8;
  --el-button-active-border-color: #2F56D8;

  // 正常态强制白字（避免全局主题覆盖 primary 文字色）
  &:not(.is-disabled) {
    color: #fff;
    :deep(span) { color: #fff; }
  }
}

/* 底部操作条 */
.st-actions {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 14px 0 4px;
}

.st-list-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.st-list-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.st-view-toggle {
  margin-left: auto;
}

@media (max-width: 1400px) {
  .st-preview-grid { grid-template-columns: 1fr; }
  .st-list-grid { grid-template-columns: 1fr; }
}
</style>

<!-- ElMessageBox 挂到 body 上，用非 scoped 样式保证确认按钮文字为白色 -->
<style lang="scss">
.st-confirm-box {
  .el-button--primary {
    color: #fff !important;
  }
}
</style>
